import React, { useLayoutEffect, useRef, useState } from "react";
import personalData from "../config/personalData";
import CommunicationsCaseStudies from "../components/CommunicationsCaseStudies";
import mariposaPreview from "../Assets/Images/mariposa-homepage-overview.jpg";

export default function Home() {
  const { site, home, theme } = personalData;
  const portraitRef = useRef(null);
  const bioRef = useRef(null);
  const pageRef = useRef(null);
  const [synopsisOpen, setSynopsisOpen] = useState(false);

  useLayoutEffect(() => {
    const portrait = portraitRef.current;
    const bio = bioRef.current;
    let frame;
    let disposed = false;
    const fitBio = () => {
      if (disposed) return;
      bio.style.width = "";
      portrait.style.height = "";
      if (!window.matchMedia("(min-width: 640px)").matches) return;
      const availableWidth = portrait.parentElement.parentElement.clientWidth - portrait.offsetWidth - 24;
      const targetHeight = portrait.offsetHeight;
      const minimumWidth = Math.min(240, availableWidth);
      const maximumWidth = Math.min(480, availableWidth);
      let bestWidth = maximumWidth;
      let bestScore = Infinity;
      const text = bio.firstChild;
      const wordRanges = [...text.textContent.matchAll(/\S+/g)].map((word) => {
        const range = document.createRange();
        range.setStart(text, word.index);
        range.setEnd(text, word.index + word[0].length);
        return range;
      });
      // Prefer the portrait's height, but penalize visibly stretched word spaces.
      for (let width = maximumWidth; width >= minimumWidth; width -= 3) {
        bio.style.width = `${width}px`;
        const rects = wordRanges.flatMap((range) => [...range.getClientRects()]);
        let largestGap = 0;
        for (let index = 1; index < rects.length; index += 1) {
          if (Math.abs(rects[index].top - rects[index - 1].top) < 1) {
            largestGap = Math.max(largestGap, rects[index].left - rects[index - 1].right);
          }
        }
        const score = Math.abs(bio.offsetHeight - targetHeight) * 4 + Math.max(0, largestGap - 10) * 12;
        if (score < bestScore) {
          bestScore = score;
          bestWidth = width;
        }
      }
      bio.style.width = `${bestWidth}px`;
      // Whole text lines cannot fill an arbitrary height through width alone.
      // Match the frame to the fitted paragraph to keep both edges exact.
      portrait.style.height = `${bio.offsetHeight}px`;
    };
    const scheduleFit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fitBio);
    };
    const observer = new ResizeObserver(scheduleFit);
    observer.observe(portrait);
    observer.observe(portrait.parentElement.parentElement);
    window.addEventListener("resize", scheduleFit);
    document.fonts.ready.then(scheduleFit);
    fitBio();
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleFit);
    };
  }, [home.bio]);

  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !window.IntersectionObserver) return;
    const sections = [...pageRef.current.querySelectorAll("[data-reveal]")];
    const observer = new IntersectionObserver((entries) => {
      let revealIndex = 0;
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.setProperty("--reveal-delay", `${Math.min(revealIndex * 90, 180)}ms`);
          entry.target.classList.add("is-visible");
          revealIndex += 1;
        } else if (entry.boundingClientRect.bottom <= 0 || entry.boundingClientRect.top >= window.innerHeight) {
          // Re-arm only after the entire section has left the screen.
          entry.target.classList.remove("is-visible");
          entry.target.style.setProperty("--reveal-delay", "0ms");
        }
      });
    }, { threshold: 0 });
    sections.forEach((section) => {
      section.classList.add("reveal-ready");
      observer.observe(section);
    });
    return () => {
      observer.disconnect();
      sections.forEach((section) => {
        section.classList.remove("reveal-ready", "is-visible");
        section.style.removeProperty("--reveal-delay");
      });
    };
  }, []);

  return (
    <main ref={pageRef} className="mx-auto max-w-6xl px-6 pb-24 pt-10 text-center md:pt-14">
      <section data-reveal className="mb-24 md:mb-32">
        <div className="flex items-center justify-center gap-4 md:gap-10">
          <div
            className="motion-fade-up motion-delay-1 h-px flex-1"
            style={{ backgroundColor: theme.colors.border }}
          />
          <div className="text-center">
            <p
              className="motion-fade-up font-bodoni text-[2rem] uppercase leading-none tracking-[0.45em] md:text-[3.25rem]"
              style={{ color: theme.colors.accent }}
            >
              {site.title}
            </p>
            <p
              className="motion-fade-up motion-delay-1 mt-3 text-sm italic md:text-base"
              style={{ color: theme.colors.muted }}
            >
              {site.subtitle}
            </p>
          </div>
          <div
            className="motion-fade-up motion-delay-1 h-px flex-1"
            style={{ backgroundColor: theme.colors.border }}
          />
        </div>
      </section>

      <section className="grid grid-cols-1 items-start justify-items-center gap-24 md:gap-32">
        <div data-reveal className="mx-auto flex w-full flex-col items-center justify-center gap-6 sm:w-max sm:max-w-full sm:flex-row sm:items-start">
          <div
            ref={portraitRef}
            className="editorial-frame motion-frame-reveal relative w-full max-w-sm min-h-0 shrink-0 overflow-hidden border sm:w-[min(384px,calc((100vw-72px)/2))]"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
              animationDelay: "0.18s",
            }}
          >
            <div className="aspect-[4/5]" aria-hidden="true" />
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={home.portrait.src}
                alt={home.portrait.alt}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="motion-fade-up motion-delay-4 w-full min-w-0 text-left text-base leading-7 sm:w-auto md:text-lg">
            <p ref={bioRef} className="bio-paragraph" lang="en">{home.bio}</p>
          </div>
        </div>

        <aside className="w-full max-w-[792px] text-center">
          <h2
            data-reveal
            className="motion-fade-up motion-delay-1 text-center font-bodoni text-4xl leading-none md:text-5xl"
            style={{ color: theme.colors.accent }}
          >
            works
          </h2>
          <section data-reveal aria-labelledby="novels-heading">
          <h3
            id="novels-heading"
            className="mt-16 font-bodoni text-3xl leading-none md:mt-20 md:text-4xl"
            style={{ color: theme.colors.accent }}
          >
            novels
          </h3>
          <div
            className="motion-fade-up mx-auto mt-6 w-full max-w-[280px] md:mt-8"
          >
            <div className="book-display">
              <button
                type="button"
                className="book-volume book-preview"
                aria-label={`${home.book.title}: show or hide synopsis`}
                aria-expanded={synopsisOpen}
                aria-describedby="sea-of-butterflies-synopsis"
                onClick={() => setSynopsisOpen((open) => !open)}
                onKeyDown={(event) => { if (event.key === "Escape") setSynopsisOpen(false); }}
              >
                  <img
                    src={home.book.cover}
                    alt={`${home.book.title} book cover by Bruna Porto`}
                    className="book-cover"
                  />
                <span className="book-synopsis" id="sea-of-butterflies-synopsis">
                  {home.book.synopsis}
                </span>
              </button>
            </div>
          </div>
          <h4 className="mt-5 font-bodoni text-2xl">{home.book.title}</h4>
          <a
            href={home.book.url}
            target="_blank"
            rel="noopener noreferrer"
            className="minimal-link mt-3 inline-block pb-1 text-xs tracking-wide"
          >
            View on Amazon <span aria-hidden="true">↗</span>
          </a>
          </section>
          <section data-reveal className="mt-16 md:mt-24" aria-labelledby="mariposa-heading">
            <h3
              id="mariposa-heading"
              className="font-bodoni text-3xl leading-none md:text-4xl"
              style={{ color: theme.colors.accent }}
            >
              literacy website — mariposa books
            </h3>
            <img
              src={mariposaPreview}
              alt="Mariposa Books homepage, featuring its butterfly logo, navigation, and literary articles"
              loading="lazy"
              width={513}
              height={628}
              className="mx-auto mt-8 h-auto w-full max-w-lg"
            />
            <a
              href="https://www.mariposabookstore.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="minimal-link mt-4 inline-flex pb-1 text-xs tracking-wide"
            >
              Visit Mariposa Books <span aria-hidden="true">↗</span>
            </a>
          </section>
          <section className="mt-16 md:mt-24" aria-labelledby="communications-heading">
            <h3
              data-reveal
              id="communications-heading"
              className="font-bodoni text-3xl leading-none md:text-4xl"
              style={{ color: theme.colors.accent }}
            >
              marketing &amp; communications projects
            </h3>
            <CommunicationsCaseStudies items={personalData.communications.items} />
          </section>
        </aside>
        <section data-reveal aria-labelledby="contact-heading" className="w-full">
          <h2
            id="contact-heading"
            className="font-bodoni text-4xl leading-none md:text-5xl"
            style={{ color: theme.colors.accent }}
          >
            contact
          </h2>
          <form
            action={personalData.contactForm.formspreeEndpoint}
            method="POST"
            className="contact-form mx-auto mt-8 w-full max-w-md space-y-6 text-left"
          >
            <input type="hidden" name="_subject" value="New message from brunaporto.com" />
            <div>
              <label htmlFor="contact-name" className="mb-2 block text-sm">Name</label>
              <input id="contact-name" name="name" type="text" autoComplete="name" required maxLength={150} />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-2 block text-sm">Email</label>
              <input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={254} />
            </div>
            <div>
              <label htmlFor="contact-message" className="mb-2 block text-sm">Message</label>
              <textarea id="contact-message" name="message" rows={5} required maxLength={10000} />
            </div>
            <button type="submit" className="minimal-link inline-block pb-1 text-sm tracking-wide">
              Send message <span aria-hidden="true">↗</span>
            </button>
            <p className="text-xs leading-5" style={{ color: theme.colors.muted }}>
              or reach out directly at{" "}
              <a href="mailto:bruna@brunaporto.com" className="minimal-link inline-block">
                bruna@brunaporto.com
              </a>.
            </p>
            <div className="flex items-center justify-start gap-4" aria-label="Social media">
              <a
                href={personalData.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram: @bruporto_"
                title="Instagram: @bruporto_"
                className="inline-flex h-6 w-6 items-center justify-center opacity-75 transition-opacity hover:opacity-100 focus-visible:opacity-100"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a
                href={personalData.socials.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok: @bruporto_"
                title="TikTok: @bruporto_"
                className="inline-flex h-6 w-6 items-center justify-center opacity-75 transition-opacity hover:opacity-100 focus-visible:opacity-100"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                  <path d="M16.7 2h-3.2v13.7a3.1 3.1 0 1 1-2.7-3.1V9.3a6.4 6.4 0 1 0 6 6.4V8.8A8.1 8.1 0 0 0 22 10V6.8A5.2 5.2 0 0 1 16.7 2Z" />
                </svg>
              </a>
            </div>
          </form>
        </section>
      </section>
    </main>
  );
}
